import { collection, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "../firebase";

const TERMINAL = ["COMPLETED", "CANCELLED"];
const isLive = (d) => d && !TERMINAL.includes(d.status) && d.LastMileStatus !== "Completed";

/**
 * Picks the request a waitlist should display from a user's request docs.
 *
 * Firestore returns collection docs in document-ID order (random uniqueKeys),
 * NOT creation order, so we must choose deliberately instead of taking the
 * first non-completed doc:
 *   1. A truly live request wins (not COMPLETED, not CANCELLED, not delivered).
 *      The one-live-request guard guarantees at most one exists.
 *   2. Otherwise fall back to the user's latestRequestKey (server sets it on
 *      every create), so a just-cancelled/completed request still shows its
 *      real terminal state — never a stale sibling.
 *   3. Last resort: most recent by createdAt.
 *
 * `docs` is an array of Firestore QueryDocumentSnapshots.
 */
export function pickWaitlistDoc(docs, latestRequestKey) {
  if (!docs || docs.length === 0) return null;
  const live = docs.find((d) => isLive(d.data()));
  if (live) return live;
  if (latestRequestKey) {
    const latest = docs.find((d) => d.id === latestRequestKey);
    if (latest) return latest;
  }
  return [...docs].sort(
    (a, b) => String(b.data().createdAt || "").localeCompare(String(a.data().createdAt || ""))
  )[0];
}

/**
 * Returns { role } of an existing non-terminal (live) request for this phone,
 * or null if none. Used to enforce one live request per phone number across
 * every creation entry point (landing, selection, item/flight details).
 */
export async function findLiveRequest(phone) {
  if (!phone) return null;

  // These four reads used to run one after another, each awaiting the last —
  // up to FOUR sequential round trips, and this is usually the first Firestore
  // call after a page load, so it also paid the SDK's connection handshake.
  // AddressSelection blocks its whole form on this, so the user sat on a
  // spinner for all of it. Same reads, same billing, same precedence — just
  // issued together instead of queued.
  const [tReqs, sReqs] = await Promise.all([
    getDocs(collection(db, "users", phone, "TravelerRequests")),
    getDocs(collection(db, "users", phone, "SenderRequests")),
  ]);

  // Legacy single-doc format, only consulted for a role whose collection is
  // empty — exactly when the old code consulted it, so the read count is
  // unchanged. Promise.all over nulls resolves without a network call, so a
  // user who has requests on both sides still costs just the one round trip.
  const [tLegacy, sLegacy] = await Promise.all([
    tReqs.empty ? getDoc(doc(db, "users", phone, "Traveler", "details")) : null,
    sReqs.empty ? getDoc(doc(db, "users", phone, "Sender", "details")) : null,
  ]);

  // Evaluation order is deliberately unchanged: traveller (collection, else
  // legacy) wins over sender, as before.
  if (!tReqs.empty) {
    if (tReqs.docs.some((d) => isLive(d.data()))) return { role: "TRAVELER" };
  } else if (tLegacy && tLegacy.exists() && isLive(tLegacy.data())) {
    return { role: "TRAVELER" };
  }

  if (!sReqs.empty) {
    if (sReqs.docs.some((d) => isLive(d.data()))) return { role: "SENDER" };
  } else if (sLegacy && sLegacy.exists() && isLive(sLegacy.data())) {
    return { role: "SENDER" };
  }

  return null;
}
